import React from 'react';
import { clsx } from 'clsx';
import { format, differenceInDays, isPast } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';

/**
 * Priority-to-border-color mapping for the left accent stripe.
 */
const PRIORITY_BORDER_COLORS = {
  critical: 'border-l-red-600',
  high: 'border-l-orange-500',
  medium: 'border-l-amber-500',
  standard: 'border-l-slate-400',
};

/**
 * Priority badge labels displayed in uppercase.
 */
const PRIORITY_LABELS = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  standard: 'Standard',
};

/**
 * Category display labels.
 */
const CATEGORY_LABELS = {
  POLICY: 'Policy Directive',
  STATUTORY: 'Statutory Submission',
  ADMIN: 'Administrative',
  LEGISLATION: 'Legislation',
  EMERGENCY: 'Emergency Response',
  BUDGET: 'Budget Allocation',
  RATIFICATION: 'Ratification',
};

/**
 * Returns the Tailwind color class for the progress bar fill
 * based on the percentage value.
 * @param {number} progress - 0 to 100
 * @returns {string} Tailwind bg class
 */
const getProgressColor = (progress) => {
  if (progress >= 70) return 'bg-grn-green';
  if (progress >= 30) return 'bg-amber-500';
  return 'bg-red-500';
};

/**
 * DecisionCard -- renders a single cabinet decision as a card.
 *
 * Used on the CabinetDecisions list page (full variant) and
 * on the Dashboard widget (compact variant).
 *
 * @param {Object}   props
 * @param {Object}   props.decision         - CabinetDecision entity
 * @param {Array}    props.directives       - Directives belonging to this decision
 * @param {'full'|'compact'} props.variant  - Layout variant
 * @param {Function} props.onViewDetails    - Called when "View Details" is clicked
 * @param {Function} props.onViewDirectives - Called when "View Directives" is clicked
 * @param {Function} props.onEscalate       - Called when "Escalate" is clicked
 */
export const DecisionCard = ({
  decision,
  directives = [],
  variant = 'full',
  onViewDetails,
  onViewDirectives,
  onEscalate,
}) => {
  const priority = decision.priority || 'standard';
  const borderColor = PRIORITY_BORDER_COLORS[priority] || PRIORITY_BORDER_COLORS.standard;
  const progress = decision.overallProgress ?? 0;
  const overdueCount = decision.overdueDirectives ?? 0;
  const totalDirectives = decision.totalDirectives ?? directives.length;
  const completedDirectives = decision.completedDirectives ?? 0;
  const attachmentCount = decision.attachments?.length ?? 0;

  // Deadline helpers
  const deadlineDate = decision.deadline ? new Date(decision.deadline) : null;
  const daysUntilDeadline = deadlineDate ? differenceInDays(deadlineDate, new Date()) : null;
  const isOverdue = deadlineDate ? isPast(deadlineDate) && decision.status !== 'Completed' : false;

  if (variant === 'compact') {
    return <CompactCard decision={decision} progress={progress} borderColor={borderColor} onViewDetails={onViewDetails} />;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.3 }}
    >
      <Card
        className={clsx(
          'border-l-4 hover:shadow-lg transition-all duration-300 overflow-hidden',
          borderColor
        )}
      >
        {/* Badges row */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Badge status={PRIORITY_LABELS[priority]}>
            {PRIORITY_LABELS[priority]?.toUpperCase()}
          </Badge>

          <Badge status={decision.category === 'POLICY' ? 'Published' : 'Pending'}>
            {CATEGORY_LABELS[decision.category] || decision.category}
          </Badge>

          <Badge status={decision.status}>
            {decision.status}
          </Badge>

          {overdueCount > 0 && (
            <Badge status="Overdue">
              <SafeIcon icon={FiIcons.FiAlertTriangle} className="mr-1 inline-block" size={12} />
              {overdueCount} Overdue Directive{overdueCount > 1 ? 's' : ''}
            </Badge>
          )}
        </div>

        {/* Title and description */}
        <div className="mb-4">
          <h3 className="text-base font-semibold text-foreground leading-snug">
            {decision.referenceNumber} &mdash; {decision.title}
          </h3>
          {decision.description && (
            <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
              &ldquo;{decision.description}&rdquo;
            </p>
          )}
        </div>

        {/* Metadata grid */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <MetadataCell
            icon={FiIcons.FiBriefcase}
            label="Lead Ministry"
            value={decision.responsibleMinistries?.find((m) => m.role === 'Lead')?.ministryName || 'Not assigned'}
          />
          <MetadataCell
            icon={FiIcons.FiCalendar}
            label="Cabinet Sitting"
            value={decision.dateDecided ? format(new Date(decision.dateDecided), 'dd MMM yyyy') : 'N/A'}
          />
          <MetadataCell
            icon={FiIcons.FiClock}
            label="Deadline"
            value={deadlineDate ? format(deadlineDate, 'dd MMM yyyy') : 'No deadline'}
            valueClassName={clsx(
              isOverdue && 'text-red-600 font-semibold',
              !isOverdue && daysUntilDeadline !== null && daysUntilDeadline <= 14 && 'text-amber-600 font-medium'
            )}
          />
          <MetadataCell
            icon={FiIcons.FiGitBranch}
            label="Directives"
            value={`${totalDirectives} total, ${completedDirectives} completed${overdueCount > 0 ? `, ${overdueCount} overdue` : ''}`}
          />
        </div>

        {/* Progress bar */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Implementation Progress
            </span>
            <span className="text-xs font-semibold text-foreground">{progress}%</span>
          </div>
          <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
            <motion.div
              className={clsx('h-full rounded-full', getProgressColor(progress))}
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            />
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border">
          {onViewDetails && (
            <Button
              variant="outline"
              size="sm"
              icon={FiIcons.FiEye}
              onClick={() => onViewDetails(decision)}
            >
              View Details
            </Button>
          )}

          {onViewDirectives && (
            <Button
              variant="outline"
              size="sm"
              icon={FiIcons.FiGitBranch}
              onClick={() => onViewDirectives(decision)}
            >
              View Directives
            </Button>
          )}

          {onEscalate && decision.status !== 'Completed' && (
            <Button
              variant="destructive"
              size="sm"
              icon={FiIcons.FiAlertTriangle}
              onClick={() => onEscalate(decision)}
            >
              Escalate
            </Button>
          )}

          {attachmentCount > 0 && (
            <span className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground">
              <SafeIcon icon={FiIcons.FiPaperclip} size={14} />
              {attachmentCount} Document{attachmentCount > 1 ? 's' : ''}
            </span>
          )}
        </div>
      </Card>
    </motion.div>
  );
};

/**
 * Compact variant -- minimal card for dashboard widgets.
 */
const CompactCard = ({ decision, progress, borderColor, onViewDetails }) => {
  const priority = decision.priority || 'standard';

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.2 }}
      className="cursor-pointer"
      onClick={() => onViewDetails?.(decision)}
    >
      <Card
        className={clsx(
          'border-l-4 hover:shadow-md transition-all duration-200',
          borderColor
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Badge status={decision.status} size="sm">
                {decision.status}
              </Badge>
              <Badge status={PRIORITY_LABELS[priority]} size="sm">
                {PRIORITY_LABELS[priority]}
              </Badge>
            </div>
            <h4 className="text-sm font-medium text-foreground truncate">
              {decision.referenceNumber} &mdash; {decision.title}
            </h4>
            <p className="text-xs text-muted-foreground mt-0.5">
              {decision.responsibleMinistries?.find((m) => m.role === 'Lead')?.ministryName || 'Unassigned'}
            </p>
          </div>

          <div className="flex flex-col items-end gap-1 shrink-0">
            <span className="text-xs font-semibold text-foreground">{progress}%</span>
            <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={clsx('h-full rounded-full', getProgressColor(progress))}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>

        {decision.deadline && (
          <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
            <SafeIcon icon={FiIcons.FiClock} size={12} />
            <span>Due {format(new Date(decision.deadline), 'dd MMM yyyy')}</span>
          </div>
        )}
      </Card>
    </motion.div>
  );
};

/**
 * Metadata cell used inside the full-variant metadata grid.
 */
const MetadataCell = ({ icon, label, value, valueClassName }) => (
  <div className="bg-muted/50 rounded-lg p-3">
    <div className="flex items-center gap-1.5 mb-1">
      <SafeIcon icon={icon} size={14} className="text-muted-foreground" />
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
    <p className={clsx('text-sm font-medium text-foreground', valueClassName)}>
      {value}
    </p>
  </div>
);
